// DeviceModalSettings.js 

import React, { useState, useEffect, useCallback } from 'react';
import ReactModal from 'react-modal';
import { CircularProgress } from '@mui/material';
import BackendService from "../../services/backend.service.js";
import DatabaseService from '../../services/database.service.js';
import "../../Modal.css";
import { convertDateToString } from '../../common/ConvertTools.js';
import { onRequestExport, findClassNameByBleId, getSettingDescriptionById, getListStringsByBleId } from '../../common/Settings.js';
import { toast } from 'react-toastify';
import SettingRenderer from './SettingRenderer';
import DeviceModalSettingReason from './DeviceModalSettingReason';
import { VESC_ADC_5V } from '../../common/Settings.js';

ReactModal.setAppElement('#root');

const EXCLUDED_BLE_IDS = [42, 43, 77, 78, 79, 142]; // IDs to exclude

const WARING_VESC_ADC_5V = "Warning : your VESC may not support 5V ADC input. Be sure to check your VESC specifications before applying this setting."

const DeviceModalSettings = ({ isOpen, isAdmin, onRequestClose, deviceProps }) => {
    const [settingsValues, setSettingsValues] = useState([]);
    const [initialValues, setInitialValues] = useState({}); // Store initial values for each setting
    const [settingsRaw, setSettingsRaw] = useState([]);
    const [settingsUpdateDate, setSettingsUpdateDate] = useState(0);
    const [modifiedSettings, setModifiedSettings] = useState({});
    const [loading, setLoading] = useState(true);
    const [isDeviceSettingModalReasonOpen, setIsDeviceSettingModalReasonOpen] = useState(false);
    const [reason, setReason] = useState('');

    const isDark = localStorage.getItem('themeDark');
    const backgroundColorModal = isDark === "true" ? "#1f2023" : "#ddd";
    const textColor = isDark === "true" ? "#ddd" : "#000";

    const [settingsDescription, setSettingsDescription] = useState({});

    const handleReasonChange = useCallback((e) => {
        setReason(e.target.value);
    }, []);

    const handleSettingChange = useCallback((bleId, newValue, originalValue) => {
        
        if ((bleId == VESC_ADC_5V) && (originalValue == 0) && (newValue == 1)) {
            alert(WARING_VESC_ADC_5V);
        }

        setModifiedSettings(prevState => ({
            ...prevState,
            [bleId]: { value: newValue, type: "blur" }
        }));
    }, []);


    const resetSetting = useCallback((bleId) => {
        setModifiedSettings(prevState => {
            const newState = { ...prevState };
            delete newState[bleId];
            return newState;
        });
    }, []);

    const saveModifiedSettingsInDb = useCallback(() => {
        const settingsToSave = Object.entries(modifiedSettings).map(([bleId, modifiedValue]) => ({
            bleId,
            value: modifiedValue.value, // Extract only the raw value here
            editedAt: Date.now()
        }));

        DatabaseService.editDeviceSettingsPush(deviceProps?.mac_custom, deviceProps?.user_id, settingsToSave, reason)
            .then(response => {
                if (response.status === 200 || response.status === 201 || response.status === 204) {
                    toast.success("Settings edited successfully", {
                        position: "bottom-right",
                        autoClose: 2000
                    });
                } else {
                    alert("Failed to save settings.");
                }
            })
            .catch(error => {
                console.error("Error saving settings:", error);
                alert("An error occurred while saving settings.");
            });
    }, [modifiedSettings, reason, deviceProps?.mac_custom, deviceProps?.user_id]);


    const openDeviceSettingModalReason = useCallback(() => {
        setIsDeviceSettingModalReasonOpen(true);
    }, []);

    const closeDeviceSettingModalReason = useCallback(() => {
        setIsDeviceSettingModalReasonOpen(false);
    }, []);

    useEffect(() => {
        if (deviceProps) {
            setLoading(true);

            BackendService.getSettingsDevice(deviceProps?.mac_custom)
                .then((response) => {
                    if (response.data.success) {
                        const settingsRaw = response.data.enrichedSettings;
                        setSettingsRaw(settingsRaw);
                        setSettingsUpdateDate(response.data.date);

                        const settingsDescriptionLocal = response.data.settingsDescription;
                        setSettingsDescription(settingsDescriptionLocal);

                        const formattedData = settingsRaw.map((value, index) => ({
                            id: index,
                            section: findClassNameByBleId(settingsDescriptionLocal, value.bleId),
                            description: getSettingDescriptionById(settingsDescriptionLocal, value.bleId),
                            valueStr: getListStringsByBleId(settingsDescriptionLocal, value.bleId, value.value),
                            valueIndex: value.value,
                            isValid: value.isValid,
                            ...value,
                        }));

                        // Store initial values to allow "reset to old value"
                        const initialValues = formattedData.reduce((acc, setting) => {
                            acc[setting.bleId] = setting.value;
                            return acc;
                        }, {});
                        setInitialValues(initialValues);

                        setSettingsValues(formattedData);

                        return DatabaseService.getDeviceSettingsPush(deviceProps?.mac_custom);
                    } else {
                        setSettingsValues([]);
                        setSettingsUpdateDate(0);
                        setLoading(false);
                    }
                })
                .then(response => {
                    if (response) {
                        const waitingSettings = response.data[0]?.settings_values || [];
                        const pendingChanges = {};
                        waitingSettings.forEach(setting => {
                            pendingChanges[setting.bleId] = setting.value;
                        });
                        setModifiedSettings(pendingChanges);
                    }
                    setLoading(false);
                })
                .catch((error) => {
                    console.error("Error fetching settings:", error);
                    setLoading(false);
                });
        }
    }, [deviceProps]);

    const groupedSettings = settingsValues.reduce((groups, setting) => {
        const index = groups.findIndex(group => group.name === setting.section);
        if (index === -1) {
            groups.push({ name: setting.section, settings: [setting] });
        } else {
            groups[index].settings.push(setting);
        }
        return groups;
    }, []);

    const options = { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric' };
    const dateStr = new Date(settingsUpdateDate * 1000).toLocaleDateString('en', options);

    const inputStyles = {
        border: '1px solid #ccc',
        borderRadius: '0.25rem',
        padding: '0.2rem 0.2rem',
        paddingLeft: '0.5rem',
        marginLeft: '10px',
        backgroundColor: backgroundColorModal,
        color: textColor,
        boxSizing: 'border-box'
    };

    const selectStyles = {
        padding: '5px 0.1rem',
        outline: 'none',
    };

    const handleBlur = (bleId, originalValue, currentValue, e) => {
        if (String(currentValue).trim() === String(originalValue).trim()) {
            resetSetting(bleId);
            e.target.style.border = '1px solid #ccc';
        } else {
            e.target.style.border = '1px solid red';
        }
    };

    const handleImportSettings = async () => {
        try {
            // Prompt the user to select a JSON file
            const [fileHandle] = await window.showOpenFilePicker({
                types: [{ description: 'JSON Files', accept: { 'application/json': ['.json'] } }],
                multiple: false
            });
    
            const file = await fileHandle.getFile();
            const text = await file.text();
            const importedSettings = JSON.parse(text);
    
            // Define the VESC 5V ADC input ID
            const VESC_ADC_5V_BLE_ID = 120;
    
            // Update settings and track modifications with "blur" type
            const newSettingsValues = settingsValues.map(existingSetting => {
                // Find the corresponding imported setting based on bleId
                const importedSetting = importedSettings.find(setting => setting.bleId === existingSetting.bleId);
    
                // If no imported setting or excluded bleId, retain the current value
                if (!importedSetting || EXCLUDED_BLE_IDS.includes(existingSetting.bleId)) {
                    return existingSetting;
                }
    
                // Prepare the new value and check for modification
                const isModified = importedSetting.value !== existingSetting.value;
                if (isModified) {
                    setModifiedSettings(prev => ({
                        ...prev,
                        [existingSetting.bleId]: { value: importedSetting.value, type: "blur" }
                    }));
    
                    // Check for the specific alert condition for VESC 5V ADC input
                    if (
                        existingSetting.bleId === VESC_ADC_5V_BLE_ID &&
                        existingSetting.value === 0 &&
                        importedSetting.value === 1
                    ) {
                        alert(WARING_VESC_ADC_5V);
                    }
                }
    
                // Return the updated setting for UI rendering
                return {
                    ...existingSetting,
                    value: importedSetting.value,
                    valueStr: getListStringsByBleId(settingsDescription, existingSetting.bleId, importedSetting.value),
                    valueIndex: importedSetting.value
                };
            });
    
            setSettingsValues(newSettingsValues);
    
            toast.success("Settings imported successfully", {
                position: "bottom-right",
                autoClose: 2000
            });
        } catch (error) {
            console.error("Error importing settings:", error);
            toast.error("Failed to import settings", { position: "bottom-right" });
        }
    };
    


    return (
        <>
            {/* Only show the primary modal if the secondary modal is not open */}
            {!isDeviceSettingModalReasonOpen && (
                <ReactModal
                    isOpen={isOpen}
                    onRequestClose={onRequestClose}
                    contentLabel="DeviceModalSettings"
                    className="modal-content"
                    overlayClassName="modal-overlay"
                    style={{
                        content: {
                            height: '95vh', // Ensure the modal uses 95% of the viewport height
                            maxHeight: '95vh', // Prevent the modal from exceeding 95% height
                            display: 'flex',
                            flexDirection: 'column',
                        },
                    }}
                >
                    <div className="modal-header">
                        <div style={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', alignItems: 'center' }}>
                            <h5 style={{ fontWeight: 'bold', paddingRight: '5px', whiteSpace: 'nowrap' }}>
                                {deviceProps?.ble_name ? `${deviceProps?.ble_name} ` : ""}
                            </h5>
                            <h7 style={{ whiteSpace: 'nowrap', paddingBottom: '5px' }}>
                                {deviceProps?.mac_custom}
                            </h7>
                        </div>

                        <div style={{ display: 'flex' }}>
                            {isAdmin &&
                                <button
                                    className="btn btn-outline-primary m-1"
                                    onClick={() => {
                                        onRequestExport(deviceProps, settingsRaw);
                                    }}
                                    style={{ marginRight: '10px' }}
                                >
                                    <i className="fas fa-file-export"></i>
                                </button>
                            }
                            <button
                                className="btn btn-outline-primary m-1"
                                onClick={handleImportSettings} // Trigger import on button click
                            >
                                <i className="fas fa-file-import"></i>
                            </button>
                            <button
                                className="btn btn-outline-primary m-1"
                                onClick={openDeviceSettingModalReason}
                                style={{ marginRight: '10px' }}
                            >
                                <i className="fas fa-save"></i>
                            </button>
                            <button
                                className="btn btn-outline-primary m-1"
                                onClick={onRequestClose}
                                style={{ marginRight: '10px' }}
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                    </div>

                    {loading ? (
                        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexGrow: 1 }}>
                            <CircularProgress />
                        </div>
                    ) : (
                        <div style={{ overflowY: 'auto', flexGrow: 1, padding: '10px' }}>
                            {settingsUpdateDate > 0 && (
                                <div style={{ marginBottom: '10px' }}>
                                    Updated: {dateStr}
                                </div>
                            )}
                            <ul style={{ listStyleType: 'none', padding: 0 }}>
                                {groupedSettings.length > 0 ? (
                                    groupedSettings.map((group, groupIndex) => (
                                        <SettingRenderer
                                            key={groupIndex}
                                            group={group}
                                            modifiedSettings={modifiedSettings}
                                            handleSettingChange={handleSettingChange}
                                            handleBlur={handleBlur}
                                            resetSetting={resetSetting}
                                            initialValues={initialValues} // Pass initial values
                                            inputStyles={inputStyles}
                                            selectStyles={selectStyles}
                                            deviceProps={deviceProps}
                                        />
                                    ))
                                ) : (
                                    <div>Settings not available</div>
                                )}
                            </ul>
                        </div>
                    )}
                </ReactModal>
            )}

            {/* Render DeviceSettingModalReason, hide the primary modal while this is open */}
            {isDeviceSettingModalReasonOpen && (
                <DeviceModalSettingReason
                    isOpen={isDeviceSettingModalReasonOpen}
                    onRequestClose={closeDeviceSettingModalReason}
                    onSave={saveModifiedSettingsInDb}
                    reason={reason}
                    handleReasonChange={handleReasonChange}
                    backgroundColorModal={backgroundColorModal}
                    textColor={textColor}
                    inputStyles={{
                        border: '1px solid #ccc',
                        borderRadius: '0.25rem',
                        padding: '0.2rem 0.2rem',
                        paddingLeft: '0.5rem',
                        marginLeft: '10px',
                        backgroundColor: backgroundColorModal,
                        color: textColor,
                        boxSizing: 'border-box'
                    }}
                />
            )}
        </>
    );
};

export default DeviceModalSettings;
